import React from 'react';
import styled, { css } from 'styled-components';
import Link from 'next/link';
import { observer } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import EmbeddedExternalLink from 'src/components/core/embedded-external-link';
import useUI from 'src/hooks/use-ui';
import { DEFAULT_SPECIALS_CARD_IMG } from 'shared/constants';
import { replaceAWSSourceWithImgix } from 'shared/helpers/imgix';
import {
  formatSpecialsCardDaysLeft,
  getSpecialsCardDaysLeft,
  SPECIALS_CARD_ENDING_SOON_THRESHOLD_DAYS,
} from '@dutchie/common/utils/specials-card';

type SpecialOfferCardSpecial = {
  _id?: string | null;
  name?: string | null;
  menuDisplayName?: string | null;
  menuDisplayImage?: string | null;
  specialType?: string | null;
  endStamp?: string | null;
  parentId?: string | null;
};

type SpecialOfferCardProps = {
  special: SpecialOfferCardSpecial;
  href: string;
  showDaysLeft?: boolean;
  role?: string;
  'aria-label'?: string;
  'aria-roledescription'?: string;
};

export const SpecialOfferCard = observer(
  ({
    special,
    href,
    showDaysLeft = true,
    role,
    'aria-label': ariaLabel,
    'aria-roledescription': ariaRoleDescription,
  }: SpecialOfferCardProps): JSX.Element => {
    const { menuDisplayName, menuDisplayImage, parentId } = special;
    const flags = useFlags();
    const { isEmbeddedCarousel } = useUI();
    // In an embedded carousel the card lives in a cross-origin iframe: a plain next/link
    // navigates the iframe itself. EmbeddedExternalLink dispatches `route:changing` so the
    // host page navigates instead (same as View All, product cards, and the legacy offer card).
    const LinkWrapper = isEmbeddedCarousel ? EmbeddedCardLink : CardLink;

    const multiTierDiscountsEnabled = flags['core.cats-pos.multi-tier-discounts.rollout'] ?? false;
    const hasParent = multiTierDiscountsEnabled && parentId != null;

    const daysLeft = showDaysLeft ? getSpecialsCardDaysLeft(special.endStamp) : null;
    const isEndingSoon = daysLeft !== null && daysLeft <= SPECIALS_CARD_ENDING_SOON_THRESHOLD_DAYS;

    const imageSrc = replaceAWSSourceWithImgix(menuDisplayImage) || DEFAULT_SPECIALS_CARD_IMG;

    return (
      <LinkWrapper
        href={href}
        data-testid='special-offer-card'
        role={role}
        aria-label={ariaLabel}
        aria-roledescription={ariaRoleDescription}
      >
        {hasParent && <MultiTierBadge>Buy More Save More</MultiTierBadge>}

        <ImageSection data-testid='special-offer-card-image'>
          <CardImage src={imageSrc} alt={menuDisplayName ?? 'Special offer'} loading='lazy' />
        </ImageSection>

        <InfoSection>
          <TitleSection>
            <TextContent>
              <Title>{menuDisplayName}</Title>
              {daysLeft !== null && (
                <CountdownRow>
                  <CountdownText>{formatSpecialsCardDaysLeft(daysLeft)}</CountdownText>
                  {isEndingSoon && <EndingSoonBadge>Ending soon!</EndingSoonBadge>}
                </CountdownRow>
              )}
            </TextContent>
          </TitleSection>
        </InfoSection>
      </LinkWrapper>
    );
  }
);

SpecialOfferCard.displayName = 'SpecialOfferCard';

const cardLinkStyles = css`
  align-items: center;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: var(--dcm-radius-cards, 12px);
  color: inherit;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: var(--dcm-special-card-carousel-height, 234px);
  max-width: 358px;
  min-width: 358px;
  padding: 16px;
  position: relative;
  text-decoration: none;
`;

const CardLink = styled(Link)`
  ${cardLinkStyles}
`;

const EmbeddedCardLink = styled(EmbeddedExternalLink)`
  ${cardLinkStyles}
`;

// Same pill styling as special-card's "Members Only" badge, reused here for
// visual consistency across the specials feature's card components. Every
// themeable value is exposed as a --dcm-* custom property (see
// src/theme/core-menu-variables.ts) so remote Ecommerce Pro SDK themes can
// restyle or reposition the badge without overriding the component outright.
const MultiTierBadge = styled.span`
  background: var(--dcm-specials-multi-tier-badge-bg, #00624a);
  border-radius: var(--dcm-radius-sm, 4px);
  color: var(--dcm-specials-multi-tier-badge-text-color, #fff);
  font-size: var(--dcm-specials-multi-tier-badge-font-size, 11px);
  font-weight: var(--dcm-font-weight-semibold, 600);
  line-height: 1;
  padding: var(--dcm-specials-multi-tier-badge-padding, 4px 8px);
  position: absolute;
  right: var(--dcm-specials-offer-card-multi-tier-badge-offset-right, 12px);
  text-transform: var(--dct--customized-textTransforms-tags, uppercase);
  top: var(--dcm-specials-offer-card-multi-tier-badge-offset-top, 12px);
  white-space: nowrap;
  z-index: var(--dcm-specials-multi-tier-badge-z-index, 1);
`;

const ImageSection = styled.div`
  aspect-ratio: var(--dcm-special-card-image-ratio, 286 / 124);
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
`;

const CardImage = styled.img`
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  min-width: 0;
  width: 100%;
`;

const TitleSection = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  min-width: 0;
  width: 100%;
`;

const TextContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.p`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--dcm-special-card-title-lines, 2);
  color: ${({ theme }) => theme.colors?.primaryBlack ?? '#121516'};
  display: -webkit-box;
  font-size: var(--dcm-special-card-title-font-size, 20px);
  font-weight: 600;
  line-height: var(--dcm-special-card-title-line-height, 24px);
  overflow: hidden;
`;

const CountdownRow = styled.div`
  align-items: center;
  display: flex;
  font-size: 12px;
  gap: 8px;
  letter-spacing: 0.06px;
`;

const CountdownText = styled.span`
  color: #485055;
  white-space: nowrap;
`;

const EndingSoonBadge = styled.span`
  color: #f35000;
  font-weight: 500;
`;
